import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { toast } from 'sonner';
import { PlatformVendorInvitation, VendorInvitationStats } from '@/lib/types/vendorInvitation';

export const useVendorInvitations = () => {
  const [invitations, setInvitations] = useState<PlatformVendorInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [dailyCount, setDailyCount] = useState(0);
  const [stats, setStats] = useState<VendorInvitationStats>({
    total: 0,
    pending: 0,
    accepted: 0,
    expired: 0,
    acceptanceRate: 0
  });

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('platform_vendor_invitations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInvitations((data || []) as PlatformVendorInvitation[]);
      
      // Calculate stats
      const total = data?.length || 0;
      const pending = data?.filter(i => i.status === 'pending').length || 0;
      const accepted = data?.filter(i => i.status === 'accepted').length || 0;
      const expired = data?.filter(i => i.status === 'expired').length || 0;
      const acceptanceRate = total > 0 ? Math.round((accepted / total) * 100) : 0;
      
      setStats({ total, pending, accepted, expired, acceptanceRate });
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast.error('Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('get_daily_invitation_count', {
        p_user_id: user.id
      });

      if (error) throw error;
      setDailyCount(data || 0);
    } catch (error) {
      console.error('Error fetching daily count:', error);
    }
  };

  const checkExistingVendor = async (email: string): Promise<boolean> => {
    try {
      // Use Edge Function to securely check if email is registered as a vendor
      const { data, error } = await invokeEdgeFunction('check-vendor-exists', {
        body: { email },
        callerOperation: 'useVendorInvitations.checkExistingVendor',
      });

      if (error) {
        console.error('Error checking existing vendor:', error);
        return false;
      }

      const result = data as { exists?: boolean };
      return result?.exists ?? false;
    } catch (error) {
      console.error('Error checking existing vendor:', error);
      // If check fails, return false to allow the invitation attempt
      // Database constraints will handle actual duplicates
      return false;
    }
  };

  const sendInvitation = async (email: string, personalMessage?: string): Promise<void> => {
    try {
      // Validate message length
      if (personalMessage && personalMessage.length > 500) {
        throw new Error('Personal message cannot exceed 500 characters');
      }

      // Check daily limit
      if (dailyCount >= 10) {
        throw new Error('Daily invitation limit reached (10 per day)');
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's organization
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('Organization not found');
      }

      // Check if email is already registered as vendor
      const isExistingVendor = await checkExistingVendor(email);
      if (isExistingVendor) {
        throw new Error('This email is already registered as a vendor on the platform');
      }

      // Check for existing pending invitation from this user
      const { data: existingInvitation } = await supabase
        .from('platform_vendor_invitations')
        .select('id')
        .eq('invited_email', email.toLowerCase())
        .eq('invited_by_user_id', user.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (existingInvitation) {
        throw new Error('You already have a pending invitation to this email');
      }

      // Create invitation
      const { data: invitation, error: inviteError } = await supabase
        .from('platform_vendor_invitations')
        .insert({
          invited_email: email.toLowerCase(),
          invited_by_user_id: user.id,
          inviter_organization_id: profile.organization_id,
          personal_message: personalMessage || null,
          status: 'pending',
        })
        .select()
        .single();

      if (inviteError) throw inviteError;

      // Get organization name for email
      const { data: orgData } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', profile.organization_id)
        .single();

      // Queue invitation email
      await invokeEdgeFunction('queue-email', {
        body: {
          recipient_email: email.toLowerCase(),
          template_name: 'vendor_platform_invitation',
          template_data: {
            inviter_name: user.email,
            organization_name: orgData?.name || 'A client organization',
            personal_message: personalMessage,
            signup_url: `${window.location.origin}/auth/signup?role=vendor`,
          },
          send_immediately: true,
        },
        callerOperation: 'useVendorInvitations.sendInvitation',
      });

      await fetchInvitations();
      await fetchDailyCount();

      toast.success(`Invitation sent to ${email}`);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast.error(error.message || 'Failed to send invitation');
      throw error;
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('platform_vendor_invitations')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitationId)
        .eq('status', 'pending');

      if (error) throw error;

      await fetchInvitations();

      toast.success('Invitation cancelled');
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('Failed to cancel invitation');
      throw error;
    }
  };

  const reInvite = async (email: string, personalMessage?: string) => {
    return sendInvitation(email, personalMessage);
  };

  useEffect(() => {
    fetchInvitations();
    fetchDailyCount();
  }, []);

  return {
    invitations,
    loading,
    dailyCount,
    stats,
    sendInvitation,
    cancelInvitation,
    reInvite,
    refetch: () => {
      fetchInvitations();
      fetchDailyCount();
    },
    checkExistingVendor,
  };
};
